import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { IMG, scrollToHash } from "../lib/site";

const LINKS = [
  { label: "The Story", path: "/#story", testId: "nav-link-story", hash: "#story" },
  { label: "Collections", path: "/collections", testId: "nav-link-collections" },
  { label: "Bespoke Atelier", path: "/configurator", testId: "nav-link-configurator" },
  { label: "Gallery", path: "/gallery", testId: "nav-link-gallery" },
  { label: "Stone & Craft", path: "/craftsmanship", testId: "nav-link-craftsmanship" },
  { label: "Hospitality", path: "/hospitality", testId: "nav-link-hospitality" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const goStory = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToHash("#story"), 450);
    } else {
      scrollToHash("#story");
    }
    setOpen(false);
  };

  const linkCls = (isActive) =>
    `whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.13em] transition-colors duration-200 ${
      isActive ? "text-amber-400" : "text-stone-300 hover:text-amber-400"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled
          ? "border-amber-900/25 bg-[#080706]/90 shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-10">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <img
            src={IMG.logo}
            alt="Cozy Ember Co. crest"
            className="h-12 w-12 rounded-full border border-amber-500/40 object-cover shadow-ember-sm transition-transform duration-500 group-hover:rotate-6"
          />
          <span className="leading-none">
            <span className="block font-serif text-lg font-semibold tracking-wide text-stone-50">
              Cozy <span className="text-gold-gradient">Ember</span>
            </span>
            <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.32em] text-amber-500/80">
              Handcrafted Outdoor Luxury
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-5 lg:flex" data-testid="nav-desktop">
          {LINKS.map((l) =>
            l.hash ? (
              <a key={l.testId} href={l.path} onClick={goStory} data-testid={l.testId} className={linkCls(false)}>
                {l.label}
              </a>
            ) : (
              <NavLink key={l.testId} to={l.path} data-testid={l.testId} className={({ isActive }) => linkCls(isActive)}>
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden items-center gap-5 lg:flex">
          <Link
            to="/configurator"
            data-testid="header-commission-cta"
            className="hidden whitespace-nowrap rounded-full bg-gradient-to-r from-amber-600 to-amber-700 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-950 shadow-ember-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-ember xl:inline-block"
          >
            Commission a Table
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="text-stone-200 lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-amber-900/25 bg-[#0a0806]/97 backdrop-blur-xl lg:hidden">
          <nav className="flex flex-col gap-1 px-6 py-6">
            {LINKS.map((l) =>
              l.hash ? (
                <a
                  key={l.testId}
                  href={l.path}
                  onClick={goStory}
                  data-testid={`${l.testId}-mobile`}
                  className="rounded-lg px-3 py-3 font-serif text-xl text-stone-200 transition-colors duration-200 hover:bg-amber-950/30 hover:text-amber-400"
                >
                  {l.label}
                </a>
              ) : (
                <NavLink
                  key={l.testId}
                  to={l.path}
                  data-testid={`${l.testId}-mobile`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 font-serif text-xl transition-colors duration-200 ${
                      isActive ? "text-amber-400" : "text-stone-200 hover:bg-amber-950/30 hover:text-amber-400"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
            <NavLink
              to="/contact"
              data-testid="nav-link-contact-mobile"
              className="rounded-lg px-3 py-3 font-serif text-xl text-stone-200 transition-colors duration-200 hover:bg-amber-950/30 hover:text-amber-400"
            >
              Commission
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
};
